import {
  dehydrate,
  HydrationBoundary,
  QueryClient,
} from "@tanstack/react-query";
import { ContentPreview, CuratedSelectionCompilation } from "@turtlewave/types";
import { type LoaderFunctionArgs, type MetaFunction } from "react-router";

import { MoreContentButton } from "../compilations/components/MoreContentButton";
import { useCompilationContent } from "../compilations/hooks/useCompilationContent";
import { compilationQuery } from "../compilations/queries";
import { SectionHeader } from "../components/SectionHeader";
import { ContentErrorBoundary } from "../content/components/ContentErrorBoundary";
import { ContentList } from "../content/components/ContentList";
import { contentListQuery } from "../content/queries";
import { LatestUpdateBanner } from "../highlights/components/LatestUpdateBanner";
import { useDehydratedState } from "../hooks/useDehydratedState";
import { getBrowserQueryClient } from "../providers/queryClient";
import { Loadable } from "../types/loadable";
import { apiUnreachableInDev, loaderQueryClient } from "../utils/loaders";
import { canonical } from "../utils/meta";

async function primeSelectedWorks(client: QueryClient) {
  const compilation = await client.ensureQueryData(
    compilationQuery(CuratedSelectionCompilation),
  );
  await client.ensureQueryData(
    contentListQuery({
      compilationId: CuratedSelectionCompilation,
      sort: compilation.default_sort,
    }),
  );
}

export async function loader({ context }: LoaderFunctionArgs) {
  const queryClient = loaderQueryClient(context);
  await primeSelectedWorks(queryClient).catch((error: unknown) => {
    if (!apiUnreachableInDev(error)) throw error;
  });
  return { dehydratedState: dehydrate(queryClient) };
}

// Starts the fetch and returns: the page renders now and its hooks fill it
// in, the list following once the compilation's sort is known.
export function clientLoader() {
  void getBrowserQueryClient().prefetchQuery(
    compilationQuery(CuratedSelectionCompilation),
  );
  return { dehydratedState: null };
}

export const meta: MetaFunction = () => [
  { title: "TurtleWave" },
  {
    name: "description",
    content: "TurtleWave - Understanding, Refinement, Humanity",
  },
  canonical("/"),
];

export function Home() {
  const { content, isLoading, error } = useCompilationContent(
    CuratedSelectionCompilation,
  );

  const contentQuery: Loadable<ContentPreview[]> = {
    data: content.length > 0 ? content : undefined,
    isLoading,
    error,
  };

  return (
    <div className="w-full px-6 py-12 md:px-12 lg:ml-8">
      <LatestUpdateBanner />
      <section aria-label="List of Selected Artworks">
        <SectionHeader title="SELECTED WORKS" />

        <ContentErrorBoundary>
          <ContentList
            query={contentQuery}
            compilationId={CuratedSelectionCompilation}
          />
        </ContentErrorBoundary>
      </section>

      <div className="sticky bottom-6 mt-12 flex justify-center">
        <MoreContentButton label="SEE MORE WORK" />
      </div>
    </div>
  );
}

export default function HomeRoute() {
  const dehydratedState = useDehydratedState();
  return (
    <HydrationBoundary state={dehydratedState}>
      <Home />
    </HydrationBoundary>
  );
}
